import type { ClipStore, ReviewDocument } from "@spikeeasy/review";
import { api } from "./api";

export type ReviewStatus = "awaiting_upload" | "in_review" | "done" | "cancelled";
export type VideoState = "none" | "waiting" | "processing" | "playable" | "failed";

export interface ReviewSummary {
  id: string;
  status: ReviewStatus;
  playerName: string | null;
  playerEmail?: string;
  note: string | null;
  free: boolean;
  createdAt: number;
  uploadedAt: number | null;
  dueAt: number | null;
  doneAt: number | null;
  coach: { id: string; name: string | null; email: string | null } | null;
  video: { state: VideoState; failure: string | null; canReplace: boolean };
}

export interface CoachReviewData {
  review: ReviewSummary;
  document: ReviewDocument;
  documentVersion: number;
  canEdit: boolean;
  canPick: boolean;
}

export const videoUrl = (reviewId: string) => `/api/reviews/${reviewId}/video`;

interface ClipUpload {
  clipId: string;
  partBytes: number;
  partCount: number;
  etags: Map<number, string>;
}

const RETRY_DELAYS_MS = [1000, 3000, 8000, 20000];

/** PUTs one part to its presigned R2 URL, retrying dropped connections and server errors. */
async function putPart(url: string, body: Blob): Promise<string> {
  for (let attempt = 0; ; attempt++) {
    const response = await fetch(url, { method: "PUT", body }).catch(() => null);
    const etag = response?.ok ? response.headers.get("ETag") : null;
    if (etag) return etag;
    // A 403 means the 15-minute URL expired; the caller asks for fresh ones.
    if (response?.status === 403) throw new Error("expired");
    const delay = RETRY_DELAYS_MS[attempt];
    if (delay === undefined) throw new Error("The recording could not be uploaded. Check your connection and retry.");
    await new Promise((resolve) => setTimeout(resolve, delay));
  }
}

/**
 * Recordings go from the browser straight to R2 in 8 MiB parts, through URLs the app signs for one
 * part each. A retry of the same recording (the editor keeps the blob) continues with the missing parts.
 */
export function reviewClips(reviewId: string): ClipStore {
  const uploads = new WeakMap<Blob, ClipUpload>();
  const base = `/coach/reviews/${reviewId}/clips`;
  return {
    async saveClip(blob, details) {
      let upload = uploads.get(blob);
      if (!upload) {
        const started = await api<Omit<ClipUpload, "etags">>(base, {
          media: details.media,
          mime: details.mimeType,
          extension: details.extension,
          size: blob.size,
        });
        upload = { ...started, etags: new Map() };
        uploads.set(blob, upload);
      }
      for (let round = 0; upload.etags.size < upload.partCount; round++) {
        if (round > upload.partCount + 3) throw new Error("The recording could not be uploaded. Retry.");
        const missing = Array.from({ length: upload.partCount }, (_, i) => i + 1).filter((n) => !upload!.etags.has(n)).slice(0, 20);
        const { urls } = await api<{ urls: Record<number, string> }>(`${base}/${upload.clipId}/parts`, { partNumbers: missing });
        for (const n of missing) {
          try {
            const etag = await putPart(urls[n]!, blob.slice((n - 1) * upload.partBytes, n * upload.partBytes));
            upload.etags.set(n, etag);
          } catch (error) {
            if ((error as Error).message === "expired") break;
            throw error;
          }
        }
      }
      const parts = [...upload.etags].map(([partNumber, etag]) => ({ partNumber, etag }));
      const { src } = await api<{ src: string }>(`${base}/${upload.clipId}/complete`, { parts });
      uploads.delete(blob);
      return src;
    },
    resolveClip: (src) => resolveClip(reviewId, src),
  };
}

export function resolveClip(reviewId: string, src: string): string {
  return src.startsWith("clip:") ? `/api/reviews/${reviewId}/clips/${src.slice(5)}` : src;
}

const dueFormat = new Intl.DateTimeFormat("en-US", {
  weekday: "short",
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
});
export const formatWhen = (at: number) => dueFormat.format(new Date(at));

/** "Due in 5 h", "Due in 2 days" or "Overdue by 3 h", for the queue. */
export function dueLabel(dueAt: number, now = Date.now()): string {
  const diff = dueAt - now;
  const hours = Math.round(Math.abs(diff) / 3600000);
  const span = hours >= 48 ? `${Math.round(hours / 24)} days` : `${Math.max(1, hours)} h`;
  return diff >= 0 ? `Due in ${span}` : `Overdue by ${span}`;
}

export const statusLabels: Record<ReviewStatus, string> = {
  awaiting_upload: "Waiting for footage",
  in_review: "Ready to review",
  done: "Done",
  cancelled: "Cancelled",
};
